/* iter-training-v-ach-coach — Live coaching floating card.
 *
 * Positioned at 2/3 of the way across the writing field, centred vertically.
 * Falls back to bottom-right corner if the containerRef is unavailable.
 *
 * iter-coach-actions — added onActionsClick + chapterId props so the
 * ⚡ AI Actions button opens CoachHintDialog for the current chapter.
 */
import React, { useLayoutEffect, useState } from 'react';
import {
  X, Sparkles, Users, Feather, BookOpen, Milestone, Zap,
} from 'lucide-react';

const KIND_ICON = {
  character: Users,
  beat:      Milestone,
  voice:     Feather,
  bible:     BookOpen,
  pacing:    Zap,
  empty:     Sparkles,
};

const KIND_LABEL = {
  character: 'Character',
  beat:      'Beat',
  voice:     'Voice',
  bible:     'Bible',
  pacing:    'Pacing',
  empty:     'Warming up',
};

const CARD_W = 300;

/** Compute {left, top} so the card sits at 2/3 across the writing field, vertically centred. */
function useFieldPosition(containerRef, visible) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!visible) return undefined;

    function compute() {
      const el = containerRef?.current;
      if (!el) { setPos(null); return; }
      const rect = el.getBoundingClientRect();
      const left = rect.left + (rect.width * 2) / 3 - CARD_W / 2;
      const top  = rect.top  + rect.height / 2;
      setPos({ left: Math.max(8, left), top });
    }

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [containerRef, visible]);

  return pos;
}

export default function LiveCoachPopover({
  suggestion,
  loading,
  onDismiss,
  onOpenTool,
  /** Called when the ⚡ AI Actions button is clicked. */
  onActionsClick,
  /** Ref to the manuscript scroll area — used to compute placement. */
  containerRef,
}) {
  const visible = !!(suggestion || loading);
  const pos = useFieldPosition(containerRef, visible);

  if (!visible) return null;

  const kind = suggestion?.kind || 'pacing';
  const Icon = KIND_ICON[kind] || Sparkles;

  /* Fallback to bottom-right corner when the container hasn't mounted yet. */
  const posStyle = pos
    ? { position: 'fixed', left: pos.left, top: pos.top, transform: 'translateY(-50%)' }
    : { position: 'fixed', bottom: 24, right: 24 };

  return (
    <div
      style={{ ...posStyle, width: CARD_W, zIndex: 40 }}
      className="pointer-events-auto rounded-lg border border-primary/50 bg-card p-3 shadow-2xl"
      data-testid="live-coach-popover"
      data-coach-kind={kind}
    >
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-primary">
        <Icon className="h-3 w-3" />
        Coach hint · {KIND_LABEL[kind] || 'Nudge'}
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto rounded-sm p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          data-testid="live-coach-dismiss"
          aria-label="Dismiss coaching hint"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {loading ? (
        <div className="mt-2 text-[12px] italic text-muted-foreground"
             data-testid="live-coach-loading">
          Reading what you've drafted so far…
        </div>
      ) : (
        <>
          <div className="mt-1 text-[13.5px] font-semibold text-foreground"
               data-testid="live-coach-title">
            {suggestion.title}
          </div>
          {suggestion.tip && (
            <p className="mt-1 text-[12px] leading-relaxed text-foreground/85"
               data-testid="live-coach-tip">
              {suggestion.tip}
            </p>
          )}
          {/* Action row — tool button + ⚡ AI Actions */}
          <div className="mt-2 flex items-center justify-between gap-2">
            {suggestion.tool_hint ? (
              <button
                type="button"
                onClick={() => onOpenTool?.(suggestion.tool_hint)}
                className="inline-flex items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90"
                data-testid="live-coach-open-tool"
              >
                Open {suggestion.tool_hint.replace(/-/g, ' ')}
              </button>
            ) : <span />}

            {/* ⚡ AI Actions — opens CoachHintDialog */}
            {onActionsClick && (
              <button
                type="button"
                onClick={onActionsClick}
                data-testid="live-coach-ai-actions-btn"
                className="inline-flex items-center gap-1 rounded-sm border border-primary/40 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/8 transition-colors"
                title="Open AI Actions — find insertion point, adjust pacing, rewrite scene"
              >
                <Zap className="h-3 w-3" />
                AI Actions
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
